/**
 * Pilote le panneau de recherche (#search-modal).
 *
 * - Desktop (>= 1330px) : modal classique. Le trigger ouvre via showDialog(),
 *   fermeture au clic sur le fond sombre / le header / Échap.
 * - Mobile/tablette (< 1330px) : recherche « ancrée » (docked). Le vrai input
 *   est affiché en permanence à la place de .header-mobile__search ; le panneau
 *   de résultats se déploie au focus du champ (état piloté en CSS par
 *   :focus-within). Le dialog n'est JAMAIS fermé sur mobile (sinon l'input
 *   disparaîtrait) : « fermer » = blur (repli). On l'affiche via `dialog.open`
 *   (et non `show()`) pour éviter les focusing-steps qui ouvriraient le clavier
 *   au chargement.
 */
const DESKTOP_MIN_WIDTH = 1330;
const MORPH_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

function initSearchModalPanel(component) {
  const dialog = component.querySelector('dialog');
  if (!dialog) return;

  const searchInput = component.querySelector('input.search-input');
  const headerGroup = document.getElementById('header-group');
  const searchBar = component.querySelector('.predictive-search-form__header-inner');

  const isMobile = () => window.innerWidth < DESKTOP_MIN_WIDTH;

  const mirrorTypingToPlaceholder = () => {
    const label = document.querySelector('.header-menu__search-text');
    if (!label || !searchInput) return;
    const mirror = () => {
      if (isMobile()) return;
      searchInput.placeholder = label.textContent;
    };
    new MutationObserver(mirror).observe(label, {
      characterData: true,
      childList: true,
      subtree: true,
    });
    mirror();
  };

  const syncRowTop = () => {
    const trigger = document.querySelector('.header-menu__search-trigger');
    if (!trigger || isMobile() || trigger.style.position) return;
    const rect = trigger.getBoundingClientRect();
    if (!rect.height) return;
    dialog.style.setProperty('--ax-search-row-top', `${rect.top}px`);
  };

  const holdScrollbarGutter = () => {
    if (isMobile()) return;
    if (window.innerWidth - document.documentElement.clientWidth > 0) {
      document.documentElement.style.scrollbarGutter = 'stable';
    }
  };

  const morphs = [];
  let releaseTrigger = null;

  const morphSearchBar = (reverse) => {
    if (isMobile() || !searchBar) return;
    const trigger = document.querySelector('.header-menu__search-trigger');
    if (!trigger) return;

    for (const animation of morphs.splice(0)) animation.cancel();

    const label = trigger.querySelector('.header-menu__search-text');
    const icon = trigger.querySelector('.header-menu__search-icon');
    const panel = component.querySelector('predictive-search-component');
    const triggerStyle = getComputedStyle(trigger);
    const from = trigger.getBoundingClientRect();

    syncRowTop();

    const to = searchBar.getBoundingClientRect();
    const box = panel?.getBoundingClientRect();
    if (!from.width || !to.width) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const inputStyle = searchInput ? getComputedStyle(searchInput) : null;

    trigger.inert = true;
    Object.assign(trigger.style, {
      visibility: 'visible',
      position: 'fixed',
      margin: '0',
      maxWidth: 'none',
      left: `${from.left}px`,
      top: `${from.top}px`,
      width: `${from.width}px`,
      height: `${from.height}px`,
    });
    releaseTrigger = () => {
      for (const prop of [
        'visibility',
        'position',
        'margin',
        'max-width',
        'left',
        'top',
        'width',
        'height',
      ]) {
        trigger.style.removeProperty(prop);
      }
      trigger.inert = false;
      releaseTrigger = null;
    };

    const timing = {
      duration: reverse ? 340 : 540,
      easing: MORPH_EASING,
      fill: 'both',
    };
    const frames = (collapsed, expanded) =>
      reverse ? [expanded, collapsed] : [collapsed, expanded];
    const timed = (keyframes) =>
      reverse
        ? keyframes.map((frame) => ({ ...frame, offset: 1 - frame.offset })).reverse()
        : keyframes;
    const barStyle = getComputedStyle(searchBar);

    morphs.push(
      trigger.animate(
        frames(
          {
            transform: 'none',
            width: `${from.width}px`,
            height: `${from.height}px`,
            paddingInline: triggerStyle.paddingInlineStart,
            borderRadius: triggerStyle.borderRadius,
            borderWidth: triggerStyle.borderTopWidth,
            borderColor: triggerStyle.borderTopColor,
            backgroundColor: triggerStyle.backgroundColor,
            boxShadow: triggerStyle.boxShadow,
            color: triggerStyle.color,
          },
          {
            transform: `translate(${to.left - from.left}px, ${to.top - from.top}px)`,
            width: `${to.width}px`,
            height: `${to.height}px`,
            paddingInline: '0px',
            borderRadius: '0px',
            borderWidth: `0px 0px ${barStyle.borderBottomWidth} 0px`,
            borderColor: `transparent transparent ${barStyle.borderBottomColor} transparent`,
            backgroundColor: 'transparent',
            boxShadow: 'rgba(0, 0, 0, 0) 0px 0px 0px 0px',
            color: searchInput
              ? getComputedStyle(searchInput, '::placeholder').color || inputStyle.color
              : triggerStyle.color,
          },
        ),
        timing,
      ),
    );

    const swap = {
      duration: reverse ? 340 : 540,
      easing: 'linear',
      fill: 'both',
    };
    morphs.push(
      trigger.animate(
        timed([
          { opacity: 1, offset: 0 },
          { opacity: 1, offset: 0.5 },
          { opacity: 0, offset: 0.8 },
          { opacity: 0, offset: 1 },
        ]),
        swap,
      ),
    );
    morphs.push(
      searchBar.animate(
        timed([
          { opacity: 0, offset: 0 },
          { opacity: 0, offset: 0.45 },
          { opacity: 1, offset: 0.75 },
          { opacity: 1, offset: 1 },
        ]),
        swap,
      ),
    );

    if (label && inputStyle) {
      morphs.push(
        label.animate(
          frames({ fontSize: getComputedStyle(label).fontSize }, { fontSize: inputStyle.fontSize }),
          timing,
        ),
      );
    }

    if (icon) {
      morphs.push(
        icon.animate(frames({ opacity: 1 }, { opacity: 0 }), {
          duration: reverse ? 220 : 300,
          easing: MORPH_EASING,
          fill: 'both',
        }),
      );
    }

    if (panel && box) {
      const panelStyle = getComputedStyle(panel);
      const corner = panelStyle.borderBottomLeftRadius;
      const pill = `inset(${from.top - box.top}px ${box.right - from.right}px ${box.bottom - from.bottom}px ${
        from.left - box.left
      }px round ${triggerStyle.borderRadius})`;
      const row = `inset(0px 0px ${box.bottom - to.bottom}px 0px round 0px 0px ${corner} ${corner})`;
      const full = `inset(0px 0px 0px 0px round 0px 0px ${corner} ${corner})`;
      morphs.push(
        panel.animate(
          timed([
            { clipPath: pill, offset: 0 },
            { clipPath: row, offset: 0.45 },
            { clipPath: full, offset: 1 },
          ]),
          {
            duration: reverse ? 340 : 620,
            delay: reverse ? 0 : 40,
            easing: MORPH_EASING,
            fill: 'both',
          },
        ),
      );
    }
  };

  // Sur mobile, « fermer » ne doit JAMAIS masquer le dialog (l'input docked doit
  // rester visible) : on remplace closeDialog par un repli (blur + collapse). Ça
  // couvre TOUS les déclencheurs en un seul point — y compris le
  // `on:click="dialog-component/closeDialog"` déclaratif de la croix, quelle que
  // soit la phase à laquelle le framework l'a câblé. Sans ça, la croix appelait
  // dialog.close() → display:none → disparition instantanée (sans animation).
  const nativeCloseDialog = component.closeDialog.bind(component);
  component.closeDialog = () => {
    if (!isMobile()) {
      morphSearchBar(true);
      return nativeCloseDialog();
    }
    searchInput?.blur();
    dialog.removeAttribute('data-search-expanded');
    return Promise.resolve();
  };

  // Clic sur le header (hors déclencheur de recherche) → ferme la modal. Desktop
  // uniquement : en docked mobile la barre vit dans le header en permanence.
  const onHeaderClick = (event) => {
    if (isMobile()) return;
    // ⚠️ #search-modal est un ENFANT de #header-group (layout/theme.liquid) : tout
    // clic dans le panneau remonte jusqu'ici. Sans cette garde, cliquer l'input
    // (ou n'importe quel contenu du dropdown) fermait la recherche au lieu de le
    // focaliser. Le clic sur le fond assombri garde sa propre fermeture, gérée
    // par le listener `dialog` plus bas (target === dialog).
    if (component.contains(event.target)) return;
    if (event.target.closest('[on\\:click*="search-modal"], .header-mobile__search-trigger'))
      return;
    component.closeDialog();
  };

  // --- Mobile : ancrage permanent du panneau (docked) ---
  // L'état « déployé » est posé EXPLICITEMENT (data-search-expanded) sur une vraie
  // interaction utilisateur. On n'utilise pas :focus-within seul : un focus
  // programmatique au chargement (Nosto, navigateur…) déploierait le panneau
  // (« ouvert par défaut »). `userEngaged` exige un pointerdown/keydown préalable.
  let userEngaged = false;
  const markEngaged = () => {
    userEngaged = true;
  };
  dialog.addEventListener('pointerdown', markEngaged);
  dialog.addEventListener('keydown', markEngaged);

  dialog.addEventListener('focusin', () => {
    if (!isMobile()) return;
    if (!userEngaged) {
      // focus non sollicité (chargement) → on l'annule, le panneau reste docked.
      searchInput?.blur();
      return;
    }
    dialog.setAttribute('data-search-expanded', '');
  });

  dialog.addEventListener('focusout', (event) => {
    if (!isMobile()) return;
    // Clavier fermé (focus perdu dans le vide, relatedTarget null) → on GARDE le
    // panneau déployé pour laisser scroller/taper les suggestions. Sinon le simple
    // fait de baisser le clavier repliait le search (bug clavier mobile).
    if (!event.relatedTarget) return;
    // ne replie que si le focus quitte réellement le panneau (pas un saut vers
    // un résultat / une collection à l'intérieur).
    if (dialog.contains(event.relatedTarget)) return;
    dialog.removeAttribute('data-search-expanded');
  });

  // Verrou de scroll mobile. `html { overflow: hidden }` (CSS, sous [data-search-expanded])
  // ne bloque QUE le scroll utilisateur : le scroll programmatique reste permis, et
  // iOS en émet un à l'ouverture du clavier (« scroll to reveal » du champ focalisé).
  // Le panneau étant ancré sur un #header-group `sticky`, ce scroll fait défiler la
  // page sous lui. On fige donc aussi la position du document tant que le panneau est
  // déployé. `behavior: 'instant'` est obligatoire : html porte `scroll-behavior: smooth`.
  let lockedScrollY = null;
  const keepScroll = () => {
    if (lockedScrollY === null || window.scrollY === lockedScrollY) return;
    window.scrollTo({ top: lockedScrollY, left: 0, behavior: 'instant' });
  };
  const syncPageScrollLock = () => {
    const expanded = dialog.hasAttribute('data-search-expanded');
    if (expanded === (lockedScrollY !== null)) return;
    if (expanded) {
      lockedScrollY = window.scrollY;
      window.addEventListener('scroll', keepScroll, { passive: true });
      return;
    }
    window.removeEventListener('scroll', keepScroll);
    lockedScrollY = null;
  };
  new MutationObserver(syncPageScrollLock).observe(dialog, {
    attributeFilter: ['data-search-expanded'],
  });

  const resetTriggerMorph = () => {
    for (const animation of morphs.splice(0)) animation.cancel();
    releaseTrigger?.();
  };

  const dock = () => {
    if (!dialog.isConnected) return;
    resetTriggerMorph();
    // `dialog.open = true` affiche le dialog en non-modal SANS lancer les
    // focusing-steps (contrairement à show()/showModal()) → pas d'autofocus,
    // donc pas de clavier qui s'ouvre au chargement.
    if (!dialog.open) dialog.open = true;
    component.setAttribute('data-docked', '');
    dialog.removeAttribute('data-search-expanded');
    // Aucun scroll-lock en docked (la page reste scrollable) ; il est posé en CSS
    // (html:has(... [data-search-expanded])) seulement quand le panneau est déployé.
    document.documentElement.removeAttribute('scroll-lock');
    // Défensif : si quelque chose a focalisé l'input avant interaction, on le
    // défocalise pour rester docked.
    if (!userEngaged && dialog.contains(document.activeElement)) {
      document.activeElement.blur();
    }
  };

  const undock = () => {
    resetTriggerMorph();
    component.removeAttribute('data-docked');
    dialog.removeAttribute('data-search-expanded');
    if (dialog.open) dialog.close();
  };

  // Bascule docked (mobile) / fermé (desktop) selon le breakpoint.
  const syncMode = () => {
    if (isMobile()) {
      if (!component.hasAttribute('data-docked')) dock();
    } else if (component.hasAttribute('data-docked')) {
      undock();
    }
  };

  // --- Desktop : flux modal classique (inchangé) ---
  component.addEventListener('dialog:open', () => {
    if (isMobile()) return;
    morphSearchBar(false);
    headerGroup?.addEventListener('click', onHeaderClick);

    // Repartir d'un état vide propre à chaque ouverture (focus + clear + notif Nosto).
    const inputEl = component.querySelector('input.search-input');
    requestAnimationFrame(() => {
      inputEl?.focus({ preventScroll: true });
      if (inputEl) {
        inputEl.value = '';
        inputEl.dispatchEvent(new Event('input', { bubbles: true }));
      }
    });
  });

  component.addEventListener('dialog:close', () => {
    headerGroup?.removeEventListener('click', onHeaderClick);
    resetTriggerMorph();
    document.documentElement.style.removeProperty('scrollbar-gutter');
  });

  // Clic sur le fond du dialog → closeDialog (redéfini plus haut : repli sur
  // mobile, fermeture sur desktop). La croix passe par la même logique via son
  // handler déclaratif `dialog-component/closeDialog`.
  dialog.addEventListener('click', (event) => {
    if (event.target !== dialog) return;
    component.closeDialog();
  });

  // Bouton "Effacer" (croix du champ) : visible dès qu'il y a du texte ; le clic
  // vide l'input et notifie Nosto (event `input`) pour revenir à l'état vide.
  const resetButton = component.querySelector('.predictive-search__reset-button');

  const syncResetButton = () => {
    if (!resetButton || !searchInput) return;
    if (searchInput.value.trim().length > 0) {
      resetButton.removeAttribute('hidden');
    } else {
      resetButton.setAttribute('hidden', '');
    }
  };

  if (searchInput) {
    searchInput.addEventListener('input', syncResetButton);
    component.addEventListener('dialog:open', syncResetButton);
  }

  if (resetButton && searchInput) {
    resetButton.addEventListener('click', (event) => {
      // Ne pas remonter au listener "click outside" de Nosto (la croix est hors
      // du dropdown) qui replierait l'autocomplete au lieu de revenir à l'état vide.
      event.stopPropagation();
      searchInput.value = '';
      searchInput.dispatchEvent(new Event('input', { bubbles: true }));
      syncResetButton();
      searchInput.focus();
    });
  }

  // Clic sur un déclencheur de recherche du header alors que le panneau est DÉJÀ
  // ouvert/docké : showDialog() early-return (dialog.open) → ni focus ni
  // déploiement, l'utilisateur doit re-cliquer pile dans l'input. On focus donc
  // l'input pour « rechercher direct ». Le clic vaut engagement (sinon le focusin
  // mobile annulerait le focus → panneau non déployé). Cas dialog FERMÉ (desktop) :
  // c'est `dialog:open` qui focusera après l'ouverture → on ne fait rien ici.
  const focusInputOnTrigger = () => {
    holdScrollbarGutter();
    if (!dialog.open) return;
    userEngaged = true;
    if (isMobile()) dialog.setAttribute('data-search-expanded', '');
    requestAnimationFrame(() => searchInput?.focus());
  };
  for (const trigger of document.querySelectorAll(
    '[on\\:click*="search-modal"], .header-menu__search-trigger, .header-mobile__search-trigger',
  )) {
    trigger.addEventListener('click', focusInputOnTrigger);
  }

  // Le drawer mobile peint sous ce panneau : on replie le search au clic burger.
  document.addEventListener('click', (event) => {
    if (!event.target.closest?.('[data-dropdown-trigger="menu-drawer"]')) return;
    component.closeDialog();
  });

  // Init + suivi du breakpoint (docked ↔ modal).
  mirrorTypingToPlaceholder();
  syncMode();
  syncRowTop();
  window.addEventListener('resize', () => {
    syncMode();
    syncRowTop();
  });
}

customElements.whenDefined('dialog-component').then(() => {
  const component = document.getElementById('search-modal');
  if (component) initSearchModalPanel(component);
});
